/**
 * Route Share Domain
 * Lightweight route market share for the city panel bar.
 * Uses a rolling 3-tick average from the server (no quarter dependency).
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { OpCode, MessageType } from '../constants/op-codes';
import { sendToMatch } from '../services/nakama/match-send';

// =============================================================================
// TYPES
// =============================================================================

/** One airline's share on a route. */
interface RouteShareEntry {
  team_id: number;
  passengers: number;
}

/** Server response for a route share query. */
export interface RouteShareReport {
  origin_id: number;
  dest_id: number;
  total_passengers: number;
  ticks_in_window: number;
  entries: RouteShareEntry[];
}

// =============================================================================
// STORE
// =============================================================================

interface RouteShareState {
  report: RouteShareReport | null;
  error: string | null;

  setReport: (report: RouteShareReport | null) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const initialState = {
  report: null,
  error: null,
};

export const useRouteShareStore = create<RouteShareState>()(
  devtools((set) => ({
    ...initialState,

    setReport: (report) => {
      set({ report, error: null }, false, 'setReport');
    },

    setError: (error) => {
      set({ error }, false, 'setError');
    },

    reset: () => {
      set(initialState, false, 'resetRouteShare');
    },
  }))
);

// =============================================================================
// HANDLERS
// =============================================================================

/** Handles successful route share report from server. */
export function handleRouteShareReport(data: { report?: RouteShareReport }): void {
  useRouteShareStore.getState().setReport(data?.report ?? null);
}

/** Handles route share request failure. */
export function handleRouteShareFailed(data: { error?: string }): void {
  const errorMessage = data?.error || 'Failed to fetch route share data';
  useRouteShareStore.getState().setError(errorMessage);
}

// =============================================================================
// SERVICE METHODS
// =============================================================================

/**
 * Requests route share data from the server for a city pair.
 * Both originId and destId are required.
 */
export async function requestRouteShare(
  originId: number,
  destId: number
): Promise<void> {
  const store = useRouteShareStore.getState();
  store.setError(null);

  const message = {
    type: MessageType.MessageTypeRequestRouteShare,
    origin_id: originId,
    dest_id: destId,
  };

  // Not sent — the game reconnecting, or the socket refusing it: the pair says
  // the share could not be had, as it says a refusal.
  if (!(await sendToMatch(OpCode.OpCodeRequestRouteShare, message, 'a route share request'))) {
    store.setError('Not sent: the game is not connected to the match.');
  }
}
